import { hydrateDocumentChatConversation } from 'features/embeddedSidebar/sessions/documentChatConversationHydration'
import { sessionsFromRolodexEntries } from 'features/embeddedSidebar/sessions/documentChatSessionRolodex'
import {
  DOCUMENT_CHAT_CONVERSATION_FILE_PATH,
  documentChatRestoreMode,
  parseDocumentChatConversationSnapshot,
} from 'features/embeddedSidebar/sessions/documentChatSessionSchemas'
import { getDocChatTurnStatus } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatConversation'
import type {
  DocChatConversation,
  SearchStateUpdate,
} from 'features/knowledgeDocumentViewer/types'
import { useFileSignedUrlFetcher, useRolodexEntries } from 'features/rolodex'
import { useEffect, useMemo } from 'react'
import { RolodexEntryType } from 'types'

export function useDocChatRecovery({
  conversation,
  siteId,
  onSearchStateChange,
}: {
  conversation: DocChatConversation | undefined
  siteId: string
  onSearchStateChange: (update: SearchStateUpdate) => void
}): void {
  const activeTurn = conversation?.turns.find(
    (turn) => turn.id === conversation.activeTurnId
  )
  const recovering =
    activeTurn?.restoredRunning &&
    getDocChatTurnStatus(activeTurn) === 'loading'
  const conversationId = recovering ? conversation?.id : undefined
  const turnId = recovering ? activeTurn.id : undefined
  const query = useMemo(
    () =>
      conversationId
        ? {
            ids: [conversationId],
            type: RolodexEntryType.POWER_DOC_SEARCH_CONVERSATION,
            tags: { site: siteId },
            limit: 1,
          }
        : undefined,
    [conversationId, siteId]
  )
  const { entries } = useRolodexEntries(query, {
    allowCache: false,
    swrOptions: { refreshInterval: conversationId ? 2000 : 0 },
  })
  const { fetchSignedUrl } = useFileSignedUrlFetcher()
  const session = useMemo(
    () =>
      sessionsFromRolodexEntries(entries, siteId).find(
        (candidate) => candidate.key === conversationId
      ),
    [entries, siteId, conversationId]
  )

  useEffect(() => {
    if (
      !conversationId ||
      !turnId ||
      !session ||
      documentChatRestoreMode(session) === 'running'
    )
      return
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined
    const recover = async () => {
      try {
        const signed = await fetchSignedUrl({
          queryOrQueries: {
            entry_id: conversationId,
            path: DOCUMENT_CHAT_CONVERSATION_FILE_PATH,
          },
        })
        const response = signed?.url
          ? await fetch(signed.url, { cache: 'no-store' })
          : undefined
        const snapshot = response?.ok
          ? parseDocumentChatConversationSnapshot(await response.json())
          : null
        const restored = hydrateDocumentChatConversation({
          session,
          snapshot,
          force: true,
        })
        const settled = restored?.turns.find((turn) => turn.id === turnId)
        if (
          !restored ||
          !settled ||
          getDocChatTurnStatus(settled) === 'loading'
        )
          return
        if (cancelled) return
        onSearchStateChange((current) => {
          const chat = current.docChat
          if (
            chat?.siteId !== siteId ||
            chat.conversation?.id !== conversationId ||
            chat.conversation.activeTurnId !== turnId
          )
            return {}
          const active = chat.conversation.turns.find(
            (turn) => turn.id === turnId
          )
          if (
            !active?.restoredRunning ||
            getDocChatTurnStatus(active) !== 'loading'
          )
            return {}
          return {
            docChat: {
              ...chat,
              conversation: { ...restored, activeTurnId: turnId },
            },
          }
        })
        cancelled = true
      } catch {
        // A temporary snapshot download failure must not discard a saved turn.
      } finally {
        if (!cancelled)
          timer = setTimeout(() => {
            void recover()
          }, 2000)
      }
    }
    void recover()
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [
    conversationId,
    turnId,
    session,
    siteId,
    fetchSignedUrl,
    onSearchStateChange,
  ])
}
